import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProject, publishProject } from '../../services/projects';
import { getProjectPages, updatePage } from '../../services/pages';
import { downloadHTML } from '../../services/deploy';
import { usePreviewGenerator } from '../../hooks/usePreviewGenerator';
import { uploadPreviewImage } from '../../services/storage';
import AppHeader from '../../components/AppHeader';
import ContextualActionBar from '../../components/ContextualActionBar';
import PageCanvas from './components/PageCanvas';
import SettingsPanel from './components/SettingsPanel';
import SettingsDrawer from './components/SettingsDrawer';
import SectionLibraryModal from './components/SectionLibraryModal';
import ViewportSwitcher from './components/ViewportSwitcher';
import UndoRedoControls from './components/UndoRedoControls';
import PageSelector from './components/PageSelector';
import Icon from '../../components/AppIcon';
import config from '../../config';

const PageBuilder = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const { generatePreview } = usePreviewGenerator();
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Unified project state: all pages with sections in one place
  const [pages, setPages] = useState([]);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [currentViewport, setCurrentViewport] = useState('desktop');

  // History: each entry = { pages, pageId } — full project snapshot + which page was active
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [publishStatus, setPublishStatus] = useState('');

  // Overlay state
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [insertAtIndex, setInsertAtIndex] = useState(null);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);

  // Derived state
  const currentPage = pages.find(p => p.id === currentPageId) || null;
  const sections = currentPage?.sections || [];

  // Load project and pages from Firestore
  useEffect(() => {
    async function loadData() {
      if (!projectId) {
        // Fallback to localStorage for backward compatibility
        const savedSections = localStorage.getItem('pageBuilderSections');
        if (savedSections) {
          try {
            const parsed = JSON.parse(savedSections);
            const fallbackPage = { id: 'local', name: 'Страница', slug: '', sections: parsed, order: 0 };
            setPages([fallbackPage]);
            setCurrentPageId('local');
            setHistory([{ pages: [fallbackPage], pageId: 'local' }]);
          } catch (e) {
            console.error('Failed to load saved sections:', e);
          }
        }
        setLoading(false);
        return;
      }

      try {
        const project = await getProject(projectId);
        setCurrentProject(project);

        const loadedPages = await getProjectPages(projectId);
        const pagesWithSections = loadedPages.map(p => ({
          ...p,
          sections: p.sections || [],
        }));
        setPages(pagesWithSections);
        if (pagesWithSections.length > 0) {
          setCurrentPageId(pagesWithSections[0].id);
          setHistory([{ pages: pagesWithSections, pageId: pagesWithSections[0].id }]);
        }
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [projectId]);

  // --- History ---
  const addToHistory = (newPages, pageId = currentPageId) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ pages: newPages, pageId });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setHasUnsavedChanges(true);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const entry = history[newIndex];
      setHistoryIndex(newIndex);
      setPages(entry.pages);
      if (entry.pageId !== currentPageId) {
        setCurrentPageId(entry.pageId);
      }
      setHasUnsavedChanges(true);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const entry = history[newIndex];
      setHistoryIndex(newIndex);
      setPages(entry.pages);
      if (entry.pageId !== currentPageId) {
        setCurrentPageId(entry.pageId);
      }
      setHasUnsavedChanges(true);
    }
  };

  // --- Helper: update sections of current page ---
  const updateCurrentPageSections = (newSections) => {
    const newPages = pages.map(p =>
      p.id === currentPageId ? { ...p, sections: newSections } : p
    );
    setPages(newPages);
    addToHistory(newPages);
  };

  // --- Page switching (no save needed) ---
  const handlePageChange = (page) => {
    setCurrentPageId(page.id);
    setSelectedSectionId(null);
    setShowSettingsDrawer(false);
  };

  // Structural page changes (create/delete/rename) from PageSelector
  const handlePagesUpdate = (updatedPages, switchToPage = null) => {
    setPages(updatedPages);
    addToHistory(updatedPages, switchToPage?.id || currentPageId);
    if (switchToPage) {
      setCurrentPageId(switchToPage.id);
      setSelectedSectionId(null);
      setShowSettingsDrawer(false);
    }
  };

  // --- Section handlers ---
  const handleAddSection = (component, atIndex = null) => {
    const defaultContent = component?.isRegistrySection && component?.defaultData
      ? component.defaultData
      : {};

    const newSection = {
      id: `section-${Date.now()}`,
      type: component?.id,
      name: component?.name,
      content: defaultContent,
      styles: {
        paddingTop: 64,
        paddingBottom: 64,
        backgroundColor: '#FFFFFF'
      },
      visibility: {
        mobile: true,
        tablet: true,
        desktop: true
      },
      widgetConfig: component?.id?.startsWith('widget') ? {
        locationId: '',
        cabinetId: '',
        language: 'ru',
        showPrices: true
      } : null
    };

    let newSections;
    if (atIndex !== null && atIndex >= 0) {
      newSections = [...sections];
      newSections.splice(atIndex, 0, newSection);
    } else {
      newSections = [...sections, newSection];
    }

    updateCurrentPageSections(newSections);
    setSelectedSectionId(newSection.id);
    setShowSettingsDrawer(true);
  };

  const handleSelectSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setShowSettingsDrawer(true);
  };

  const handleUpdateSection = (sectionId, updatedSection) => {
    if (!sectionId) {
      setSelectedSectionId(null);
      setShowSettingsDrawer(false);
      return;
    }

    const newSections = sections.map(section =>
      section.id === sectionId ? updatedSection : section
    );
    updateCurrentPageSections(newSections);
  };

  const handleMoveSection = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sections.length) return;
    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);
    updateCurrentPageSections(newSections);
  };

  const handleDeleteSection = (sectionId) => {
    if (window.confirm('Удалить эту секцию?')) {
      const newSections = sections.filter(section => section.id !== sectionId);
      updateCurrentPageSections(newSections);
      if (selectedSectionId === sectionId) {
        setSelectedSectionId(null);
        setShowSettingsDrawer(false);
      }
    }
  };

  const handleDuplicateSection = (sectionId) => {
    const sectionToDuplicate = sections.find(s => s.id === sectionId);
    if (!sectionToDuplicate) return;

    const duplicated = {
      ...JSON.parse(JSON.stringify(sectionToDuplicate)),
      id: `section-${Date.now()}`,
      name: `${sectionToDuplicate.name} (копия)`,
    };

    const index = sections.findIndex(s => s.id === sectionId);
    const newSections = [...sections];
    newSections.splice(index + 1, 0, duplicated);

    updateCurrentPageSections(newSections);
    setSelectedSectionId(duplicated.id);
    setShowSettingsDrawer(true);
  };

  const handleInsertSection = (atIndex) => {
    setInsertAtIndex(atIndex);
    setShowLibraryModal(true);
  };

  const handleLibraryAddSection = (component) => {
    handleAddSection(component, insertAtIndex);
    setInsertAtIndex(null);
  };

  // --- Save / Preview / Publish / Export ---
  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (projectId) {
        // Save all pages to Firestore
        await Promise.all(pages.map(page =>
          updatePage(projectId, page.id, { sections: page.sections })
        ));
      } else {
        // Fallback to localStorage
        localStorage.setItem('pageBuilderSections', JSON.stringify(sections));
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving:', error);
      alert('Ошибка при сохранении');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    localStorage.setItem('previewSections', JSON.stringify(sections));
    if (projectId) {
      navigate(`/projects/${projectId}/preview`);
    } else {
      navigate('/dashboard');
    }
  };

  const getPublishedUrl = (project) => {
    if (!project) return null;

    const domainType = project.domainType || project.settings?.domainType;
    const customDomain = project.customDomain || project.settings?.customDomain;
    const subdomain = project.subdomain || project.settings?.subdomain;
    const selectedDomain = project.selectedDomain || project.settings?.selectedDomain;

    if (domainType === 'custom' && customDomain) {
      return `https://${customDomain}`;
    }

    if (subdomain) {
      const domain = selectedDomain || config.baseDomain;
      return `https://${subdomain}.${domain}`;
    }

    return `${window.location.origin}/p/${project.id}`;
  };

  const handlePublish = async () => {
    if (!projectId) {
      alert('Сначала сохраните сайт');
      return;
    }

    if (hasUnsavedChanges) {
      if (window.confirm('У вас есть несохраненные изменения. Сохранить перед публикацией?')) {
        await handleSave();
      }
    }

    setIsPublishing(true);
    let ogImageUrl = null;

    try {
      const projectData = await getProject(projectId);
      const theme = projectData?.settings?.theme || 'default';
      const key = projectData.subdomain || projectData.customDomain;

      if (key && sections.length > 0) {
        setPublishStatus('Генерация превью...');
        const previewBlob = await generatePreview(sections, theme);

        if (previewBlob) {
          setPublishStatus('Загрузка превью...');
          try {
            const result = await uploadPreviewImage(key, previewBlob);
            ogImageUrl = result.url;
          } catch (previewError) {
            console.warn('Failed to upload preview image:', previewError);
          }
        }
      }

      setPublishStatus('Публикация сайта...');

      const publishedPages = pages.map(page => ({
        id: page.id,
        name: page.name,
        slug: page.slug,
        order: page.order,
        sections: page.sections,
      }));

      await publishProject(projectId, publishedPages, ogImageUrl);

      const updatedProject = await getProject(projectId);
      setCurrentProject(updatedProject);

      const url = getPublishedUrl(updatedProject);
      setPublishedUrl(url);
      setPublishStatus('');
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Ошибка публикации');
      setPublishStatus('');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleExport = () => {
    downloadHTML({ sections }, {
      title: currentPage?.name || currentProject?.name || 'Page',
      filename: 'index.html',
      theme: currentProject?.settings?.theme || 'default'
    });
  };

  const handleLogout = async () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('У вас есть несохраненные изменения. Выйти без сохранения?')) {
        return;
      }
    }
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const selectedSection = sections.find(s => s.id === selectedSectionId);

  return (
    <div className="h-screen flex flex-col bg-background">
      <AppHeader
        user={{ name: user?.displayName || 'Пользователь', email: user?.email }}
        currentProject={currentProject}
        onLogout={handleLogout}
      />
      {/* mt-14 for mobile (single tier), lg:mt-[104px] for desktop (two tiers) */}
      <div className="flex-1 flex flex-col overflow-hidden mt-14 lg:mt-[104px]">
        {/* Toolbar bar */}
        <div className="p-4 bg-card border-b border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            {projectId && pages.length > 0 && (
              <PageSelector
                pages={pages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onPagesUpdate={handlePagesUpdate}
                projectId={projectId}
              />
            )}
            <ViewportSwitcher
              currentViewport={currentViewport}
              onViewportChange={setCurrentViewport}
            />
            <UndoRedoControls
              canUndo={historyIndex > 0}
              canRedo={historyIndex < history.length - 1}
              onUndo={handleUndo}
              onRedo={handleRedo}
            />
            <button
              onClick={() => {
                setInsertAtIndex(null);
                setShowLibraryModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Icon name="Plus" size={16} />
              <span className="hidden sm:inline">Добавить секцию</span>
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="Layers" size={16} />
            <span className="data-text">{sections.length} секций</span>
            {hasUnsavedChanges && (
              <span className="flex items-center gap-1 text-warning">
                <Icon name="AlertCircle" size={16} />
                <span className="hidden sm:inline">Несохраненные изменения</span>
              </span>
            )}
          </div>
        </div>

        {/* Canvas — full width */}
        <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <PageCanvas
            sections={sections}
            onSelectSection={handleSelectSection}
            selectedSectionId={selectedSectionId}
            onMoveSection={handleMoveSection}
            onDeleteSection={handleDeleteSection}
            onDuplicateSection={handleDuplicateSection}
            onInsertSection={handleInsertSection}
            currentViewport={currentViewport}
            theme={currentProject?.settings?.theme || 'default'}
          />
        </div>
      </div>

      {/* Settings Drawer overlay */}
      <SettingsDrawer
        isOpen={showSettingsDrawer && !!selectedSection}
        onClose={() => {
          setShowSettingsDrawer(false);
          setSelectedSectionId(null);
        }}
        title={selectedSection?.name || 'Настройки секции'}
      >
        <SettingsPanel
          selectedSection={selectedSection}
          onUpdateSection={handleUpdateSection}
          allSections={sections}
          allPages={pages}
          projectId={projectId}
        />
      </SettingsDrawer>

      {/* Section Library Modal overlay */}
      <SectionLibraryModal
        isOpen={showLibraryModal}
        onClose={() => {
          setShowLibraryModal(false);
          setInsertAtIndex(null);
        }}
        onAddSection={handleLibraryAddSection}
      />

      <ContextualActionBar
        onSave={handleSave}
        onPreview={handlePreview}
        onPublish={handlePublish}
        onExport={handleExport}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        publishStatus="draft"
      />

      {/* Статус публикации */}
      {isPublishing && publishStatus && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 md:p-8 w-full max-w-sm text-center">
            <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4"></div>
            <p className="text-foreground font-medium">{publishStatus}</p>
          </div>
        </div>
      )}

      {/* Модалка публикации */}
      {publishedUrl && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 md:p-8 w-full max-w-md">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Сайт опубликован!
              </h3>
              <p className="text-text-secondary">
                Ваш сайт доступен по ссылке
              </p>
            </div>

            <div className="bg-muted rounded-lg p-3 mb-6">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={publishedUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-foreground outline-none"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(publishedUrl);
                    alert('Ссылка скопирована!');
                  }}
                  className="p-2 hover:bg-background rounded transition-colors"
                  title="Копировать"
                >
                  <Icon name="Copy" size={18} />
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPublishedUrl(null)}
                className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Закрыть
              </button>
              <a
                href={publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity text-center"
              >
                Открыть сайт
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageBuilder;
